import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faCircleDot,
  faDiagramProject,
  faKey,
  faUsers,
  faPlug,
  faLayerGroup,
  faUserShield,
  faAddressCard,
  faUser,
  faLaptop,
  faCreditCard,
  faFileInvoice,
  faChartLine,
  faBell,
  faBriefcase,
  faBuilding,
} from "@fortawesome/free-solid-svg-icons";

/**
 * Permission predicate for a settings menu item. Receives the `has` checker
 * from the permissions context so items can express OR-of-multiple checks.
 * Omit `permission` for items that are always visible.
 */
type PermissionCheck = (
  has: (application: string, feature: string, category: string) => boolean,
) => boolean;

export interface SettingsMenuItem {
  title: string;
  href: string;
  icon: IconDefinition;
  permission?: PermissionCheck;
  activeMatch?: string[];
}

export type SettingsCategoryKey = "app" | "personal" | "platform";

export interface SettingsCategory {
  key: SettingsCategoryKey;
  label: string;
  /** Where the profile dropdown navigates when this category is chosen. */
  defaultHref: string;
  items: SettingsMenuItem[];
  /**
   * Route prefixes this category owns, used to resolve the active category
   * from the URL. Covers nested routes that aren't themselves menu items
   * (e.g. billing add-ons, usage limit). Longest matching prefix wins.
   */
  basePaths: string[];
}

const appSettingsMenu: SettingsMenuItem[] = [
  {
    title: "General",
    icon: faBuilding,
    href: "/application-settings/general",
    permission: (has) =>
      has("APP_MANAGEMENT", "APP_CONFIGURATION", "App Defaults") ||
      has("APP_MANAGEMENT", "APP_CONFIGURATION", "Connections") ||
      has("APP_MANAGEMENT", "APP_CONFIGURATION", "Channels"),
  },
  {
    title: "Session Roles",
    icon: faUserShield,
    href: "/application-settings/session-roles",
    permission: (has) => has("APP_MANAGEMENT", "SESSION_ROLES", "Read"),
  },
  {
    title: "Connections",
    icon: faPlug,
    href: "/application-settings/connections",
    permission: (has) => has("APP_MANAGEMENT", "CONNECTIONS", "Read"),
  },
  {
    title: "Groups",
    icon: faLayerGroup,
    href: "/application-settings/groups",
    permission: (has) => has("APP_MANAGEMENT", "GROUPS", "Read"),
  },
  {
    title: "Notifications",
    icon: faBell,
    href: "/application-settings/messaging",
    permission: (has) =>
      has("APP_MANAGEMENT", "NOTIFICATION_MANAGEMENT", "Email"),
  },
];

const platformSettingsMenu: SettingsMenuItem[] = [
  {
    title: "Projects",
    icon: faDiagramProject,
    href: "/platform-settings/projects",
    permission: (has) =>
      has("PLATFORM_SETTINGS", "PROJECT_MANAGEMENT", "Read"),
  },
  {
    title: "Environment",
    icon: faCircleDot,
    href: "/platform-settings/environment",
    permission: (has) => has("PLATFORM_SETTINGS", "ENVIRONMENT_MANAGEMENT", "Read"),
  },
  {
    title: "Roles & Permissions",
    icon: faUserShield,
    href: "/platform-settings/roles-permissions",
    permission: (has) => has("PLATFORM_SETTINGS", "ROLE_MANAGEMENT", "Read"),
  },
  {
    title: "Clients",
    icon: faKey,
    href: "/platform-settings/client",
    permission: (has) => has("PLATFORM_SETTINGS", "CLIENT_MANAGEMENT", "Read"),
  },
  {
    title: "API Keys",
    icon: faKey,
    href: "/platform-settings/api-keys",
    permission: (has) => has("PLATFORM_SETTINGS", "API_KEY_MANAGEMENT", "Read"),
  },
  {
    title: "Application Users",
    icon: faUsers,
    href: "/platform-settings/application-users",
    permission: (has) =>
      has("PLATFORM_SETTINGS", "USER_MANAGEMENT", "Read") ||
      has("PLATFORM_SETTINGS", "USER_MANAGEMENT", "Invites") ||
      has("PLATFORM_SETTINGS", "USER_MANAGEMENT", "Access") ||
      has("PLATFORM_SETTINGS", "USER_MANAGEMENT", "Roles") ||
      has("PLATFORM_SETTINGS", "USER_MANAGEMENT", "Write") ||
      has("PLATFORM_SETTINGS", "USER_MANAGEMENT", "Delete"),
  },
  {
    title: "Billing & Subscription",
    icon: faCreditCard,
    href: "/platform-settings/billing",
    activeMatch: [
      "/platform-settings/billing/overview",
      "/platform-settings/billing/addons",
      "/platform-settings/billing/details",
    ],
    permission: (has) =>
      has("PLATFORM_SETTINGS", "BILLING_USAGE", "Billing") ||
      has("PLATFORM_SETTINGS", "BILLING_USAGE", "Plans") ||
      has("PLATFORM_SETTINGS", "BILLING_USAGE", "Add-ons") ||
      has("PLATFORM_SETTINGS", "BILLING_USAGE", "Preferences"),
  },
  {
    title: "Billing History",
    icon: faFileInvoice,
    href: "/platform-settings/billing/history",
    permission: (has) => has("PLATFORM_SETTINGS", "BILLING_USAGE", "Invoices"),
  },
  {
    title: "Usage",
    icon: faChartLine,
    href: "/platform-settings/usage/overview",
    activeMatch: [
      "/platform-settings/usage/overview",
      "/platform-settings/usage/limits",
      "/platform-settings/usage/platform-limits",
      "/platform-settings/usage/alerts",
    ],
    permission: (has) =>
      has("PLATFORM_SETTINGS", "BILLING_USAGE", "Usage") ||
      has("PLATFORM_SETTINGS", "BILLING_USAGE", "Alerts"),
  },
];


const personalSettingsMenu: SettingsMenuItem[] = [
  {
    title: "Personal profile",
    icon: faAddressCard,
    href: "/account-settings/general/personal-profile",
    permission: (has) => has("ACCOUNT_SETTINGS", "PROFILE_MANAGEMENT", "Read"),
  },
  {
    title: "Account",
    icon: faUser,
    href: "/account-settings/general/accounts",
    permission: (has) => has("ACCOUNT_SETTINGS", "PROFILE_MANAGEMENT", "Write"),
  },
  {
    title: "My devices",
    icon: faLaptop,
    href: "/account-settings/general/linked-devices",
  },
  {
    title: "Workspaces",
    icon: faBriefcase,
    href: "/account-settings/general/workspaces",
    permission: (has) => has("ACCOUNT_SETTINGS", "WORKSPACE_MANAGEMENT", "Read"),
  },
];

export const SETTINGS_CATEGORIES: SettingsCategory[] = [
  {
    key: "app",
    label: "App Management",
    defaultHref: "/application-settings/general",
    items: appSettingsMenu,
    basePaths: [
      "/application-settings/general",
      "/application-settings/session-roles",
      "/application-settings/email-config",
      "/application-settings/messaging",
      "/application-settings/connections",
      "/application-settings/groups",
    ],
  },
  {
    key: "platform",
    label: "Platform Settings",
    // Let the route index choose the first page the current role can open.
    defaultHref: "/platform-settings",
    items: platformSettingsMenu,
    basePaths: [
      "/platform-settings/projects",
      "/platform-settings/environment",
      "/platform-settings/client",
      "/platform-settings/api-keys",
      "/platform-settings/roles-permissions",
      "/platform-settings/application-users",
      "/platform-settings/billing",
      "/platform-settings/usage",
    ],
  },
  {
    key: "personal",
    label: "Account Settings",
    defaultHref: "/account-settings/general/personal-profile",
    items: personalSettingsMenu,
    basePaths: [
      "/account-settings/general/personal-profile",
      "/account-settings/general/accounts",
      "/account-settings/general/linked-devices",
      "/account-settings/general/workspaces",
    ],
  },
];

export function getVisibleSettingsCategories(
  has: (application: string, feature: string, category: string) => boolean,
): SettingsCategory[] {
  return SETTINGS_CATEGORIES.filter((category) =>
    category.items.some((item) => !item.permission || item.permission(has)),
  );
}

/**
 * Resolve which settings category owns a given pathname. Matches by the
 * longest owned `basePath` prefix so that nested routes (e.g. billing
 * add-ons, usage limit) resolve to their section, and overlapping trees
 * (Account's `/account-settings/general/personal-profile` vs App's
 * `/application-settings/general`) are disambiguated correctly. Defaults
 * to the App category.
 */
export function getCategoryForPath(pathname: string): SettingsCategory {
  let best: { category: SettingsCategory; length: number } | null = null;

  for (const category of SETTINGS_CATEGORIES) {
    for (const base of category.basePaths) {
      const matches = pathname === base || pathname.startsWith(`${base}/`);
      if (matches && (!best || base.length > best.length)) {
        best = { category, length: base.length };
      }
    }
  }

  return best?.category ?? SETTINGS_CATEGORIES[0];
}


export function isSettingsPath(pathname: string): boolean {
  return SETTINGS_CATEGORIES.some((category) => {
    const root = `/${category.defaultHref.split("/")[1]}`;
    return pathname === root || pathname.startsWith(`${root}/`);
  });
}
